"use client";

import { useEffect, useState } from "react";
import Preloader from "./preloader";
import { useBootReady } from "@/lib/boot-state";
import { cn } from "@/lib/utils";

/**
 * The ONE preloader instance for the whole session (spec §2). Mounted as a
 * stable root sibling by PortalSpacetimeProvider so the Lottie animation
 * loads exactly once — the app tree assembles and paints UNDERNEATH this
 * opaque overlay, and when the boot store flips ready the overlay fades out
 * (300ms) and unmounts for good (ready is sticky, so it can never return).
 */
export default function BootOverlay() {
  const ready = useBootReady();
  const [gone, setGone] = useState(false);

  // Unmount fallback for environments where transitionend never fires
  // (prefers-reduced-motion disables the transition; jsdom has no CSS engine).
  useEffect(() => {
    if (!ready || gone) return;
    const timer = setTimeout(() => setGone(true), 600);
    return () => clearTimeout(timer);
  }, [ready, gone]);

  if (gone) return null;

  return (
    <div
      data-testid="boot-overlay"
      className={cn(
        "fixed inset-0 z-[100] bg-background transition-opacity duration-300 ease-out",
        ready && "pointer-events-none opacity-0",
      )}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && ready) setGone(true);
      }}
    >
      <Preloader />
    </div>
  );
}
